import { useEffect, useState } from 'react';
import { Model } from '../types';
import './HomeScreen.css';

const HIGHLIGHTS = [
  {
    id: 'h1',
    emoji: '🔥',
    tag: 'Top Coding',
    title: 'Gemini 2.5 Pro leads coding benchmarks',
    body: '1M token context, strong on HumanEval and SWE-bench. Free in AI Studio.',
  },
  {
    id: 'h2',
    emoji: '🆓',
    tag: 'Free Tier',
    title: 'Gemini 2.0 Flash — 1.5K free API calls/day',
    body: '1M context window, no credit card needed. Best free option for long documents.',
  },
  {
    id: 'h3',
    emoji: '🧠',
    tag: 'Reasoning',
    title: 'o4-mini: near-o3 reasoning at low cost',
    body: '$1.1/1M tokens with top math and coding scores. Great for agentic tasks.',
  },
  {
    id: 'h4',
    emoji: '🛠️',
    tag: 'IDE',
    title: 'Claude 3.7 in Cursor & Windsurf',
    body: 'Extended thinking mode available in both IDEs for complex refactors.',
  },
];

interface Props {
  favorites: Set<string>;
  onSelectModel: (model: Model) => void;
  onGoExplore: () => void;
}

export function HomeScreen({ favorites, onSelectModel, onGoExplore }: Props) {
  const [topModels, setTopModels] = useState<Model[]>([]);
  const [freeModels, setFreeModels] = useState<Model[]>([]);
  const [newModels, setNewModels] = useState<Model[]>([]);
  const [favoriteModels, setFavoriteModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [topRes, freeRes, newRes] = await Promise.all([
          fetch('/api/v1/models?sortBy=score&limit=5'),
          fetch('/api/v1/models?sortBy=score&free=true&limit=5'),
          fetch('/api/v1/models?sortBy=newest&isNew=true&limit=5'),
        ]);
        const [topData, freeData, newData] = await Promise.all([
          topRes.json(), freeRes.json(), newRes.json(),
        ]);
        setTopModels(topData.data || []);
        setFreeModels(freeData.data || []);
        setNewModels(newData.data || []);
      } catch {}
    };
    load().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (favorites.size === 0) { setFavoriteModels([]); return; }
    const ids = [...favorites];
    Promise.all(
      ids.map(id =>
        fetch(`/api/v1/models/${encodeURIComponent(id)}`).then(r => r.ok ? r.json() : null)
      )
    ).then(results => setFavoriteModels(results.filter(Boolean) as Model[]));
  }, [favorites]);

  return (
    <div className="home-screen">
      <header className="home-header">
        <div className="home-title">
          <span className="home-logo">🤖</span>
          <div>
            <h1>Model Hub</h1>
            <p>Find the right AI for any task</p>
          </div>
        </div>
        <button className="explore-btn" onClick={onGoExplore}>
          🔍 Explore
        </button>
      </header>

      {/* Highlights / Updates */}
      <section className="home-section">
        <h2 className="section-title">📢 Highlights</h2>
        <div className="highlights-scroll">
          {HIGHLIGHTS.map(h => (
            <div key={h.id} className="highlight-card">
              <div className="highlight-top">
                <span className="highlight-emoji">{h.emoji}</span>
                <span className="highlight-tag">{h.tag}</span>
              </div>
              <h3 className="highlight-title">{h.title}</h3>
              <p className="highlight-body">{h.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Favorites */}
      {favoriteModels.length > 0 && (
        <section className="home-section">
          <div className="section-header">
            <h2 className="section-title">⭐ My Favorites</h2>
            <span className="section-count">{favoriteModels.length}</span>
          </div>
          <div className="model-row">
            {favoriteModels.map(m => (
              <MiniCard key={m.id} model={m} onClick={() => onSelectModel(m)} isFavorite />
            ))}
          </div>
        </section>
      )}

      {favorites.size === 0 && (
        <section className="home-section">
          <div className="empty-favorites" onClick={onGoExplore}>
            <span className="empty-icon">⭐</span>
            <p>Tap ♡ on any model to save it here</p>
            <span className="go-explore">Browse models →</span>
          </div>
        </section>
      )}

      {/* Recently Added */}
      {!loading && newModels.length > 0 && (
        <section className="home-section">
          <div className="section-header">
            <h2 className="section-title">🆕 Recently Added</h2>
            <button className="see-all" onClick={onGoExplore}>See all</button>
          </div>
          <div className="model-row">
            {newModels.map(m => (
              <MiniCard key={m.id} model={m} onClick={() => onSelectModel(m)} isNew />
            ))}
          </div>
        </section>
      )}

      {/* Top Rated */}
      <section className="home-section">
        <div className="section-header">
          <h2 className="section-title">🏆 Top Rated</h2>
          <button className="see-all" onClick={onGoExplore}>See all</button>
        </div>
        {loading ? (
          <div className="home-loading">Loading...</div>
        ) : (
          <div className="model-row">
            {topModels.map(m => (
              <MiniCard key={m.id} model={m} onClick={() => onSelectModel(m)} />
            ))}
          </div>
        )}
      </section>

      {/* Best Free */}
      <section className="home-section">
        <div className="section-header">
          <h2 className="section-title">🆓 Best Free</h2>
          <button className="see-all" onClick={onGoExplore}>See all</button>
        </div>
        {loading ? (
          <div className="home-loading">Loading...</div>
        ) : (
          <div className="model-row">
            {freeModels.map(m => (
              <MiniCard key={m.id} model={m} onClick={() => onSelectModel(m)} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function MiniCard({ model, onClick, isFavorite, isNew }: { model: Model; onClick: () => void; isFavorite?: boolean; isNew?: boolean }) {
  const score = model.quickMetrics?.overallScore;
  const pricing = model.pricing?.[0];

  return (
    <div className={`mini-card ${isFavorite ? 'is-favorite' : ''}`} onClick={onClick}>
      {(isNew || model.isNew) && <span className="mini-new">NEW</span>}
      <div className="mini-card-name">{model.name}</div>
      <div className="mini-card-dev">{model.developer}</div>
      <div className="mini-card-footer">
        {score != null && (
          <span className="mini-score">{(score * 2).toFixed(1)}</span>
        )}
        {pricing?.freeTier && <span className="mini-free">FREE</span>}
      </div>
    </div>
  );
}
